/**
 * Date formatting helpers for the Library list.
 *
 * `formatLibraryDate(iso)` renders a relative-or-short-absolute string for the Workouts table
 * Created column per the Library redesign handoff (light theme).
 *
 * Boundaries (vs. `now`):
 *   < 1 hour                                    → "just now"
 *   < 24 hours                                  → "N hour(s) ago"
 *   < 7 days                                    → "N day(s) ago"
 *   < 30 days                                   → "N week(s) ago"
 *     (special: 28–34 days old → "last month" when also in calendar previous month)
 *   < 365 days, same calendar year              → "Mon D" (e.g. "Mar 4")
 *   >= 365 days, prior year                     → "Mon D, YYYY"
 *
 * No new dependencies — uses Intl.RelativeTimeFormat + Intl.DateTimeFormat.
 */

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const monthDayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const fullDateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Format an ISO datetime for the Library Workouts table.
 * Returns '—' for invalid input so the cell never renders raw nulls.
 * `now` is injectable for testing.
 */
export function formatLibraryDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';

  const diffMs = now.getTime() - then.getTime();
  /* Future-dated rows: fall back to absolute formatting so we don't print "in N days". */
  if (diffMs < 0) {
    if (then.getFullYear() === now.getFullYear()) return monthDayFmt.format(then);
    return fullDateFmt.format(then);
  }

  if (diffMs < MS_PER_HOUR) return 'just now';

  if (diffMs < MS_PER_DAY) {
    const hours = Math.floor(diffMs / MS_PER_HOUR);
    return rtf.format(-hours, 'hour');
  }

  const days = Math.floor(diffMs / MS_PER_DAY);
  if (days < 7) {
    return rtf.format(-days, 'day');
  }

  if (days < 30) {
    /* Cosmetic edge: when the row is 28–34 days old AND falls in the previous calendar month, prefer "last month". */
    if (days >= 28 && isCalendarPreviousMonth(then, now)) return 'last month';
    const weeks = Math.floor(days / 7);
    return rtf.format(-weeks, 'week');
  }

  if (days < 365 && then.getFullYear() === now.getFullYear()) {
    return monthDayFmt.format(then);
  }

  /* 28–34 day window that crossed a calendar month but is still < 30 days from a longer-month boundary
     is handled above; here we cover the 30–34d "last month" edge that escapes the < 30 branch. */
  if (days >= 28 && days <= 34 && isCalendarPreviousMonth(then, now)) {
    return 'last month';
  }

  return fullDateFmt.format(then);
}

/** True when `then` is exactly one calendar month before `now` (ignoring day-of-month). */
function isCalendarPreviousMonth(then: Date, now: Date): boolean {
  const thenYear = then.getFullYear();
  const thenMonth = then.getMonth();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();
  if (nowMonth === 0) return thenYear === nowYear - 1 && thenMonth === 11;
  return thenYear === nowYear && thenMonth === nowMonth - 1;
}
